import type { McpResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { LibraryBig } from 'lucide-react';
import { useCallback, useState } from 'react';

import SourceTypeIcon from '@/components/icons/SourceTypeIcon';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';

import ResourceSelectionPanel from './CustomChatConfiguration/DMResourceSelectionPanel';
import SourcesPill from './SourcesPill';

interface DMSourcesPopoverProps {
    sources: McpResponseModel[];
    selectedSources: McpResponseModel[];
    allSources: McpResponseModel[];
    disabled?: boolean;
    isLoading?: boolean;
    error?: string | null;
    isSaving?: boolean;
    onToggleSource: (source: McpResponseModel) => void;
    onClose?: () => void;
}

export default function DMSourcesPopover({
    sources,
    selectedSources,
    allSources,
    disabled = false,
    isLoading = false,
    error = null,
    isSaving = false,
    onToggleSource,
    onClose
}: DMSourcesPopoverProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const isSourceSelected = useCallback(
        (source: McpResponseModel) =>
            selectedSources.some((s) => s.name === source.name),
        [selectedSources]
    );

    const handleSeeAllSources = useCallback(() => {
        setMenuOpen(false);
        setDialogOpen(true);
    }, []);

    const handleDialogChange = useCallback(
        (open: boolean) => {
            setDialogOpen(open);
            if (!open && onClose) {
                setTimeout(() => onClose(), 100);
            }
        },
        [onClose]
    );

    const handleMenuChange = useCallback(
        (open: boolean) => {
            setMenuOpen(open);
            if (!open && onClose) {
                onClose();
            }
        },
        [onClose]
    );

    const hasSources = allSources.length > 0;

    return (
        <>
            <DropdownMenu open={menuOpen} onOpenChange={handleMenuChange}>
                <DropdownMenuTrigger asChild>
                    <span className="inline-flex">
                        <SourcesPill
                            sources={selectedSources}
                            onClick={() => setMenuOpen((prev) => !prev)}
                            disabled={disabled}
                        />
                    </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    side="top"
                    sideOffset={8}
                    className="bg-background min-w-72"
                >
                    {isLoading && (
                        <p className="text-muted-foreground text-sm text-center">
                            Loading sources…
                        </p>
                    )}
                    {error && !isLoading && (
                        <p className="text-destructive text-sm text-center">
                            {error}
                        </p>
                    )}
                    {!isLoading && !error && !hasSources && (
                        <DropdownMenuItem
                            disabled
                            className="text-muted-foreground"
                        >
                            No sources available
                        </DropdownMenuItem>
                    )}
                    {!isLoading &&
                        !error &&
                        hasSources &&
                        allSources.map((source) => (
                            <DropdownMenuItem
                                key={source.name}
                                onSelect={(e) => e.preventDefault()}
                                onClick={() => onToggleSource(source)}
                                className="justify-between px-2.5"
                            >
                                <div className="flex items-center gap-2">
                                    <SourceTypeIcon
                                        configType={source.configType}
                                        className="size-4 shrink-0"
                                    />
                                    <span className="text-foreground leading-5">
                                        {source.displayName || source.name}
                                    </span>
                                </div>
                                <Switch
                                    checked={isSourceSelected(source)}
                                    className="pointer-events-none shrink-0"
                                    size="sm"
                                    tabIndex={-1}
                                />
                            </DropdownMenuItem>
                        ))}
                    {!isLoading && !error && hasSources && (
                        <>
                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                                onClick={handleSeeAllSources}
                               
                            >
                                <LibraryBig
                                   
                                />
                                <span>
                                    See all sources
                                </span>
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Full resource selection dialog */}
            <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
                <DialogContent
                    className="flex flex-col gap-0 bg-background shadow-xl p-0 border border-border w-[800px] max-w-[95vw] h-[70vh] max-h-[700px] overflow-hidden select-none"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ResourceSelectionPanel
                        sources={allSources}
                        selectedSources={selectedSources}
                        isLoading={isLoading}
                        error={error}
                        disabled={isSaving}
                        onToggleSource={onToggleSource}
                        onClose={() => handleDialogChange(false)}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
